if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdInput() {
        return this.querySelector('[name="id"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();

        // Drop-up elements
        this.dropup = this.querySelector('.sticky-atc-dropup');
        this.dropupToggle = this.querySelector('.sticky-atc-dropup__toggle');
        this.dropupPanel = this.querySelector('.sticky-atc-dropup__panel');
        this.dropupBackdrop = this.querySelector('.sticky-atc-dropup__backdrop');

        this.init();
        this.setupDropup();
        this.setupFormProxy();
      }

      // ---------- Drop-up open / close ----------

      setupDropup() {
        if (!this.dropup) return;

        this.dropupToggle.addEventListener('click', () => this.toggleDropup());
        this.dropupBackdrop.addEventListener('click', () => this.closeDropup());

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.dropup.classList.contains('is-open')) {
            this.closeDropup();
            this.dropupToggle.focus();
          }
        });

        // Option selection
        this.dropupPanel.addEventListener('click', (e) => {
          const optionBtn = e.target.closest('.sticky-atc-dropup__option');
          if (!optionBtn) return;
          this.selectDropupOption(optionBtn);
        });
      }

      toggleDropup() {
        if (this.dropup.classList.contains('is-open')) {
          this.closeDropup();
        } else {
          this.openDropup();
        }
      }

      openDropup() {
        this.dropup.classList.add('is-open');
        this.dropupToggle.setAttribute('aria-expanded', 'true');
      }

      closeDropup() {
        this.dropup.classList.remove('is-open');
        this.dropupToggle.setAttribute('aria-expanded', 'false');
      }

      selectDropupOption(optionBtn) {
        const variantId = optionBtn.dataset.variantId;

        // Update hidden input
        this.variantIdInput.value = variantId;

        // Update toggle display
        this.updateToggleDisplay({
          title: optionBtn.dataset.variantTitle,
          price: optionBtn.dataset.variantPrice,
          comparePrice: optionBtn.dataset.variantComparePrice,
        });

        // Update aria-selected
        this.dropupPanel.querySelectorAll('[role="option"]').forEach((li) => {
          const btn = li.querySelector('.sticky-atc-dropup__option');
          li.setAttribute('aria-selected', btn.dataset.variantId === variantId ? 'true' : 'false');
        });

        this.closeDropup();

        // Sync to main product form
        this.isUpdating = true;
        this.currentVariant = this.variantData.find((v) => v.id === Number(variantId));
        this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');

        if (this.mainVariantSelects) {
          const tabs = this.mainVariantSelects.querySelectorAll('.product-tab');
          if (tabs.length > 0) {
            const matchingTab = this.mainVariantSelects.querySelector(
              `.product-tab[data-variant-id="${variantId}"]`
            );
            if (matchingTab) {
              matchingTab.click();
            }
            this.isUpdating = false;
          } else {
            // Standard select/fieldset picker
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  element.querySelectorAll('option').forEach((opt) => opt.removeAttribute('selected'));
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) selectedOption.setAttribute('selected', 'selected');
                  break;
                case 'FIELDSET':
                  element.querySelectorAll('input').forEach((radio) => {
                    if (radio.value === variantOptionVal) radio.checked = true;
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { bubbles: true }));
              this.isUpdating = false;
            }, 0);
          }
        } else {
          this.isUpdating = false;
        }

        this.updatePrice();
        this.updateButton(true, '', false);
        if (!this.currentVariant) {
          this.updateButton(true, '', true);
        } else {
          this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
        }
      }

      updateToggleDisplay({ title, price, comparePrice }) {
        const titleEl = this.dropupToggle.querySelector('.sticky-atc-dropup__toggle-title');
        const pricesEl = this.dropupToggle.querySelector('.sticky-atc-dropup__toggle-prices');

        if (titleEl) titleEl.textContent = title;
        if (pricesEl) {
          let html = '';
          if (comparePrice) {
            html += `<s class="sticky-atc-dropup__price-compare">${comparePrice}</s>`;
          }
          html += `<span class="sticky-atc-dropup__price-current">${price}</span>`;
          pricesEl.innerHTML = html;
        }
      }

      // ---------- Form proxy ----------

      setupFormProxy() {
        const stickyForm = this.querySelector('.sticky-atc-bar__form');
        if (!stickyForm) return;

        const submitBtn = stickyForm.querySelector('[name="add"]');
        if (!submitBtn) return;

        submitBtn.type = 'button';

        submitBtn.addEventListener('click', () => {
          if (submitBtn.hasAttribute('disabled')) return;

          const mainForm = this.mainProductInfo?.querySelector('form[is="product-form"]');
          if (!mainForm) return;

          const mainSubmitBtn = mainForm.querySelector('[type="submit"]');
          if (mainSubmitBtn && !mainSubmitBtn.hasAttribute('disabled') && mainSubmitBtn.getAttribute('aria-disabled') !== 'true') {
            mainSubmitBtn.click();
          }
        });
      }

      // ---------- Variant data ----------

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      // ---------- Init / visibility ----------

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }
        this.productId = this.dataset.productId;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        // Gate: only show the bar once the user has scrolled past
        // the main CTA button's bottom edge (i.e. it has left the viewport
        // scrolling upward). No arbitrary pixel magic needed.
        this.hasScrolledPastCta = false;

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // CTA is out of view AND above the viewport = user scrolled past it downward
              const ctaIsAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;

              this.container.classList.toggle('sticky-atc-bar--show', ctaIsAboveViewport);
            });
          },
          { threshold: 1, rootMargin }
        );

        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      // Reads data-variant-cta-label from the dropup option matching the current variant.
      // Falls back to the theme's default "Add to cart" string when there's no dropup
      // (e.g. products with only a default variant) or the variant isn't in the panel.
      getCtaLabelForCurrentVariant() {
        const fallback = FoxTheme.variantStrings.addToCart;
        if (!this.dropupPanel || !this.currentVariant) return fallback;

        const option = this.dropupPanel.querySelector(
          `.sticky-atc-dropup__option[data-variant-id="${this.currentVariant.id}"]`
        );
        return option?.dataset.variantCtaLabel || fallback;
      }

      // ---------- Button state ----------

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.getCtaLabelForCurrentVariant();
        }
      }

      // ---------- Price update ----------

      updatePrice() {
        // Multi-variant products don't render the price block inside the sticky bar
        // (the dropup shows per-option prices instead). Bail out when markup is absent.
        const priceWrapper = this.querySelector('.f-price');
        if (!priceWrapper) return;

        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        // Unit price block is also optional — guard it.
        if (unitPriceWrapper) {
          const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');
          if (unit_price_measurement && unitPrice) {
            unitPriceWrapper.classList.remove('hidden');
            const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
              this.currentVariant.unit_price,
              moneyFormat
            )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
            unitPrice.innerHTML = unitPriceContent;
          } else {
            unitPriceWrapper.classList.add('hidden');
          }
        }
      }

      // ---------- Bidirectional sync ----------

      syncStickyBarToVariant(variant) {
        if (!variant) {
          this.updateButton(true, '', true);
          return;
        }

        this.currentVariant = variant;
        this.variantIdInput.value = variant.id;

        // Update drop-up toggle and selection state
        if (this.dropup && !this.isUpdating) {
          const matchingOption = this.dropupPanel?.querySelector(
            `.sticky-atc-dropup__option[data-variant-id="${variant.id}"]`
          );
          if (matchingOption) {
            this.updateToggleDisplay({
              title: matchingOption.dataset.variantTitle,
              price: matchingOption.dataset.variantPrice,
              comparePrice: matchingOption.dataset.variantComparePrice,
            });
            this.dropupPanel.querySelectorAll('[role="option"]').forEach((li) => {
              const btn = li.querySelector('.sticky-atc-dropup__option');
              li.setAttribute('aria-selected', btn.dataset.variantId === String(variant.id) ? 'true' : 'false');
            });
          }
        }

        this.updatePrice();
        this.updateButton(true, '', false);
        this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
      }

      syncWithMainProductForm() {
        // Path 1: Standard picker publishes variantChange through product-info.js
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          this.syncStickyBarToVariant(event.data.variant);
        });

        // Path 2: Tab-based picker (nw-product-tabs) sets input[name="id"]
        // directly and dispatches a native change event — no pubsub involved.
        const mainForm = this.mainProductInfo?.querySelector('form[is="product-form"]');
        if (mainForm) {
          const mainIdInput = mainForm.querySelector('input[name="id"]');
          if (mainIdInput) {
            mainIdInput.addEventListener('change', () => {
              if (this.isUpdating) return;

              const variantId = Number(mainIdInput.value);
              const variant = this.variantData.find((v) => v.id === variantId);
              if (variant) {
                this.syncStickyBarToVariant(variant);
              }
            });
          }
        }
      }
    }
  );
}
